import { Action, Module, Mutation, VuexModule, getModule } from 'vuex-module-decorators';
import store from '@/store';

import { Meta, Response, ULID, ResponseSingle } from '../models';
import { User } from '../models/user';
import { conduitApi } from '../api';

@Module({
	namespaced: true,
	name: 'link',
	store,
	dynamic: true
})
class UserModule extends VuexModule {
	users: User[] = [];

	user: User = {} as any;

	meta: Meta = {} as any;

	@Mutation
	setAll(users: Response<User>): void {
		this.meta = users.meta;
		this.users = users.data;
	}

	@Mutation
	set(user: ResponseSingle<User>): void {
		this.user = user.data;
	}

	@Action({ commit: 'setAll', rawError: true })
	async getAll(query: object): Promise<Response<User>> {
		try {
			const response = await conduitApi.get('/users', {
				params: query
			});
			return response.data;
		} catch (error) {
			throw error;
		}
	}

	@Action({ commit: 'setAll', rawError: true })
	async getAllProjectID(obj: { projectID: ULID; query: object }): Promise<Response<User>> {
		try {
			const response = await conduitApi.get(`/projects/${obj.projectID}/users`, {
				params: obj.query
			});
			return response.data;
		} catch (error) {
			throw error;
		}
	}

	@Action({ commit: 'setAll', rawError: true })
	async getAllInvites(query: object): Promise<Response<User>> {
		try {
			const response = await conduitApi.get('/invites', {
				params: query
			});
			return response.data;
		} catch (error) {
			throw error;
		}
	}

	@Action({ commit: 'set', rawError: true })
	async get(id: ULID): Promise<ResponseSingle<User>> {
		try {
			const response = await conduitApi.get(`/users/${id}`);
			return response.data;
		} catch (error) {
			throw error;
		}
	}

	@Action({ commit: 'setAll', rawError: true })
	async add(obj: { user: User; link: string }): Promise<void> {
		try {
			await conduitApi.post(obj.link, obj.user);
		} catch (error) {
			throw error;
		}
	}

	@Action({ rawError: true })
	async put(obj: { user: any; userId: ULID }): Promise<void> {
		try {
			await conduitApi.put(`/users/${obj.userId}`, JSON.stringify(obj.user));
		} catch (error) {
			throw error;
		}
	}

	@Action({ rawError: true })
	async delete(link: string): Promise<void> {
		try {
			await conduitApi.delete(link);
		} catch (error) {
			throw error;
		}
	}

	@Action({ rawError: true })
	async invite(user: any): Promise<void> {
		try {
			await conduitApi.post('/invites', JSON.stringify(user));
		} catch (error) {
			throw error;
		}
	}

	@Action({ rawError: true })
	async acceptInvite(obj: { user: any; id: ULID }): Promise<void> {
		try {
			await conduitApi.post(`/invites/${obj.id}/accept`, JSON.stringify(obj.user));
		} catch (error) {
			throw error;
		}
	}

	@Action({ rawError: true })
	async validate(token: string): Promise<void> {
		try {
			await conduitApi.get(`/invites/validate/${token}`);
		} catch (error) {
			throw error;
		}
	}

	@Action({ rawError: true })
	async resetPassword(obj: { user: any }): Promise<void> {
		try {
			await conduitApi.post('/password/reset', JSON.stringify(obj.user));
		} catch (error) {
			throw error;
		}
	}

	@Action({ rawError: true })
	async forgetPassword(obj: { user: any }): Promise<void> {
		try {
			await conduitApi.post('/password/forget', JSON.stringify(obj.user));
		} catch (error) {
			throw error;
		}
	}

	@Action({ rawError: true })
	async resend(id: ULID): Promise<void> {
		try {
			await conduitApi.post(`invites/${id}/resend`);
		} catch (error) {
			throw error;
		}
	}

	@Action({ rawError: true })
	async revoke(id: ULID): Promise<void> {
		try {
			await conduitApi.delete(`invites/${id}/revoke`);
		} catch (error) {
			throw error;
		}
	}

	@Action({ rawError: true })
	async linkProjects(obj: { userID: ULID; projectIDs: { itemIDs: ULID[] } }): Promise<void> {
		try {
			await conduitApi.put(`users/${obj.userID}/projects`, JSON.stringify(obj.projectIDs));
		} catch (error) {
			throw error;
		}
	}

	@Action({ rawError: true })
	async unlinkProjects(obj: { userID: ULID; projectID: ULID }): Promise<void> {
		try {
			await conduitApi.delete(`users/${obj.userID}/projects/${obj.projectID}`);
		} catch (error) {
			throw error;
		}
	}
}

export default getModule(UserModule);
